import { useEffect, useState } from "react";
import { Edit, Eye, LogOut, Plus, Save, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "../api";
import { Logo } from "../components/Logo";

const initialPost = { title: "", excerpt: "", body: "", category_id: "", status: "published", featured: false, trending: false, hero_media_id: "", media_ids: [] };

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("vtc_admin_token") || "");
  const [login, setLogin] = useState({ email: "admin@vanniyantamil.com", password: "" });
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [comments, setComments] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [ads, setAds] = useState([]);
  const [adForm, setAdForm] = useState({ title: "", description: "", link_url: "", media_id: "", active: true });
  const [editingAdId, setEditingAdId] = useState(null);
  const [postForm, setPostForm] = useState(initialPost);
  const [editingId, setEditingId] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: "", tamil_name: "", color: "#1e3a8a" });
  const [uploading, setUploading] = useState(false);

  const loadAdmin = async () => {
    if (!localStorage.getItem("vtc_admin_token")) return;
    try {
      const [statsRes, postsRes, catRes, commentsRes, subscribersRes, adsRes] = await Promise.all([
        api.get("/admin/dashboard"),
        api.get("/posts", { params: { admin: true } }),
        api.get("/categories"),
        api.get("/admin/comments"),
        api.get("/admin/subscribers"),
        api.get("/admin/ads"),
      ]);
      setStats(statsRes.data);
      setPosts(postsRes.data);
      setCategories(catRes.data);
      setComments(commentsRes.data);
      setSubscribers(subscribersRes.data);
      setAds(adsRes.data);
      if (!postForm.category_id && catRes.data[0]) setPostForm((form) => ({ ...form, category_id: catRes.data[0].id }));
    } catch (error) {
      toast.error("Admin தரவுகளை ஏற்ற முடியவில்லை");
    }
  };

  useEffect(() => { if (token) loadAdmin(); }, [token]);

  const loginAdmin = async (event) => {
    event.preventDefault();
    try {
      const response = await api.post("/auth/login", login);
      localStorage.setItem("vtc_admin_token", response.data.token);
      setToken(response.data.token);
      toast.success("Admin login வெற்றி");
    } catch (error) {
      toast.error("Login விவரங்கள் தவறு");
    }
  };

  const logout = () => {
    localStorage.removeItem("vtc_admin_token");
    setToken("");
  };

  const uploadMedia = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const response = await api.post("/admin/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      const media = response.data;
      setPostForm((form) => ({ ...form, hero_media_id: form.hero_media_id || media.id, media_ids: [...new Set([...form.media_ids, media.id])] }));
      toast.success("Media upload முடிந்தது");
    } catch (error) {
      toast.error("Media upload தோல்வி");
    } finally {
      setUploading(false);
    }
  };

  const uploadAdMedia = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const response = await api.post("/admin/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      setAdForm((form) => ({ ...form, media_id: response.data.id }));
      toast.success("Ad banner image upload முடிந்தது");
    } catch (error) {
      toast.error("Ad banner upload தோல்வி");
    } finally {
      setUploading(false);
    }
  };

  const saveAd = async (event) => {
    event.preventDefault();
    try {
      const payload = { ...adForm, placement: "frontpage" };
      if (editingAdId) {
        await api.put(`/admin/ads/${editingAdId}`, payload);
        toast.success("Advertisement update செய்யப்பட்டது");
      } else {
        await api.post("/admin/ads", payload);
        toast.success("Advertisement publish செய்யப்பட்டது");
      }
      setEditingAdId(null);
      setAdForm({ title: "", description: "", link_url: "", media_id: "", active: true });
      loadAdmin();
    } catch (error) {
      toast.error("Advertisement save செய்ய முடியவில்லை");
    }
  };

  const editAd = (ad) => {
    setEditingAdId(ad.id);
    setAdForm({ title: ad.title, description: ad.description || "", link_url: ad.link_url || "", media_id: ad.media?.id || "", active: ad.active });
  };

  const deleteAd = async (adId) => {
    try {
      await api.delete(`/admin/ads/${adId}`);
      toast.success("Advertisement delete செய்யப்பட்டது");
      loadAdmin();
    } catch (error) {
      toast.error("Advertisement delete செய்ய முடியவில்லை");
    }
  };

  const savePost = async (event) => {
    event.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/posts/${editingId}`, postForm);
        toast.success("Post update செய்யப்பட்டது");
      } else {
        await api.post("/admin/posts", postForm);
        toast.success("Post publish செய்யப்பட்டது");
      }
      setEditingId(null);
      setPostForm({ ...initialPost, category_id: categories[0]?.id || "" });
      loadAdmin();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Post save செய்ய முடியவில்லை");
    }
  };

  const editPost = (post) => {
    setEditingId(post.id);
    setPostForm({ title: post.title, excerpt: post.excerpt, body: post.body, category_id: post.category_id, status: post.status, featured: post.featured, trending: post.trending, hero_media_id: post.hero_media?.id || "", media_ids: post.media?.map((item) => item.id) || [] });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deletePost = async (postId) => {
    try {
      await api.delete(`/admin/posts/${postId}`);
      toast.success("Post delete செய்யப்பட்டது");
      loadAdmin();
    } catch (error) {
      toast.error("Post delete செய்ய முடியவில்லை");
    }
  };

  const addCategory = async (event) => {
    event.preventDefault();
    try {
      await api.post("/admin/categories", newCategory);
      setNewCategory({ name: "", tamil_name: "", color: "#1e3a8a" });
      toast.success("Category சேர்க்கப்பட்டது");
      loadAdmin();
    } catch (error) {
      toast.error("Category சேர்க்க முடியவில்லை");
    }
  };

  const moderate = async (commentId, status) => {
    try {
      await api.put(`/admin/comments/${commentId}`, { status });
      toast.success("Comment update செய்யப்பட்டது");
      loadAdmin();
    } catch (error) {
      toast.error("Comment update செய்ய முடியவில்லை");
    }
  };

  if (!token) {
    return (
      <div className="grid min-h-screen place-items-center p-4" data-testid="admin-login-page">
        <form onSubmit={loginAdmin} className="w-full max-w-md border border-slate-200 bg-white p-8 shadow-sm" data-testid="admin-login-form">
          <Logo />
          <h1 className="mt-8 text-3xl font-black text-slate-950" data-testid="admin-login-title">Admin Login</h1>
          <div className="mt-6 grid gap-4">
            <input value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} type="email" className="border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-900" data-testid="admin-email-input" />
            <input value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} type="password" placeholder="Password" className="border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-900" data-testid="admin-password-input" />
            <button type="submit" className="bg-blue-900 px-5 py-3 font-black text-white transition-colors duration-200 hover:bg-blue-800" data-testid="admin-login-submit-button">Login</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="admin-dashboard-page">
      <header className="border-b border-slate-200 bg-white" data-testid="admin-header">
        <div className="vtc-container flex items-center justify-between py-4">
          <Logo />
          <button onClick={logout} className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 font-black text-slate-700" data-testid="admin-logout-button"><LogOut className="h-4 w-4" aria-hidden="true" />Logout</button>
        </div>
      </header>
      <main className="vtc-container space-y-8 py-8" data-testid="admin-main">
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-7" data-testid="admin-stats-grid">
          {[['Posts', stats?.posts], ['Published', stats?.published_posts], ['Comments', stats?.comments], ['Pending', stats?.pending_comments], ['Subscribers', stats?.subscribers], ['Views', stats?.views], ['Likes', stats?.likes]].map(([label, value]) => (
            <div key={label} className="border border-slate-200 bg-white p-4" data-testid={`admin-stat-${label.toLowerCase()}`}><p className="text-xs font-black uppercase tracking-[0.18em] text-green-800" data-testid={`admin-stat-${label.toLowerCase()}-label`}>{label}</p><p className="mt-2 text-3xl font-black text-blue-950" data-testid={`admin-stat-${label.toLowerCase()}-value`}>{value ?? 0}</p></div>
          ))}
        </section>

        <section className="grid gap-8 lg:grid-cols-[1fr_360px]" data-testid="admin-editor-layout">
          <form onSubmit={savePost} className="border border-slate-200 bg-white p-5 sm:p-6" data-testid="admin-post-form">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-3xl font-black text-slate-950" data-testid="admin-post-form-title">{editingId ? "Edit Post" : "Create Post"}</h2>
              {editingId && <button type="button" onClick={() => { setEditingId(null); setPostForm({ ...initialPost, category_id: categories[0]?.id || "" }); }} className="inline-flex items-center gap-2 border border-slate-300 px-3 py-2 font-black" data-testid="admin-cancel-edit-button"><X className="h-4 w-4" aria-hidden="true" />Cancel</button>}
            </div>
            <div className="mt-6 grid gap-4">
              <input value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} required placeholder="Tamil headline" className="border border-slate-300 px-4 py-3 text-xl font-black focus:outline-none focus:ring-2 focus:ring-blue-900" data-testid="admin-post-title-input" />
              <textarea value={postForm.excerpt} onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })} placeholder="Short excerpt" rows="2" className="border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-900" data-testid="admin-post-excerpt-input" />
              <textarea value={postForm.body} onChange={(e) => setPostForm({ ...postForm, body: e.target.value })} required placeholder="Full Tamil article body" rows="10" className="border border-slate-300 px-4 py-3 leading-loose focus:outline-none focus:ring-2 focus:ring-blue-900" data-testid="admin-post-body-input" />
              <div className="grid gap-4 sm:grid-cols-2">
                <select value={postForm.category_id} onChange={(e) => setPostForm({ ...postForm, category_id: e.target.value })} className="border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-900" data-testid="admin-post-category-select">
                  {categories.map((category) => <option key={category.id} value={category.id}>{category.tamil_name}</option>)}
                </select>
                <select value={postForm.status} onChange={(e) => setPostForm({ ...postForm, status: e.target.value })} className="border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-900" data-testid="admin-post-status-select"><option value="published">Published</option><option value="draft">Draft</option></select>
              </div>
              <div className="flex flex-wrap gap-4" data-testid="admin-post-flags">
                <label className="inline-flex items-center gap-2 font-bold" data-testid="admin-featured-label"><input type="checkbox" checked={postForm.featured} onChange={(e) => setPostForm({ ...postForm, featured: e.target.checked })} data-testid="admin-featured-checkbox" />Featured</label>
                <label className="inline-flex items-center gap-2 font-bold" data-testid="admin-trending-label"><input type="checkbox" checked={postForm.trending} onChange={(e) => setPostForm({ ...postForm, trending: e.target.checked })} data-testid="admin-trending-checkbox" />Trending</label>
              </div>
              <label className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-blue-900 bg-blue-50 px-4 py-5 font-black text-blue-900" data-testid="admin-upload-label">
                <Upload className="h-5 w-5" aria-hidden="true" />{uploading ? "Uploading..." : "Upload Image / Video"}
                <input type="file" accept="image/*,video/mp4,video/webm,video/quicktime" onChange={uploadMedia} className="hidden" data-testid="admin-media-upload-input" />
              </label>
              <button type="submit" className="inline-flex w-fit items-center gap-2 bg-green-800 px-5 py-3 font-black text-white transition-colors duration-200 hover:bg-green-700" data-testid="admin-save-post-button"><Save className="h-5 w-5" aria-hidden="true" />{editingId ? "Update Post" : "Publish Post"}</button>
            </div>
          </form>

          <aside className="space-y-6" data-testid="admin-side-panel">
            <form onSubmit={addCategory} className="border border-slate-200 bg-white p-5" data-testid="admin-category-form">
              <h3 className="text-2xl font-black text-slate-950" data-testid="admin-category-title">Categories</h3>
              <div className="mt-4 grid gap-3">
                <input value={newCategory.name} onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })} required placeholder="English name" className="border border-slate-300 px-4 py-3" data-testid="admin-category-name-input" />
                <input value={newCategory.tamil_name} onChange={(e) => setNewCategory({ ...newCategory, tamil_name: e.target.value })} required placeholder="Tamil name" className="border border-slate-300 px-4 py-3" data-testid="admin-category-tamil-input" />
                <button type="submit" className="inline-flex items-center justify-center gap-2 bg-blue-900 px-4 py-3 font-black text-white" data-testid="admin-add-category-button"><Plus className="h-5 w-5" aria-hidden="true" />Add Category</button>
              </div>
              <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-category-list">{categories.map((category) => <span key={category.id} className="bg-slate-100 px-3 py-1 text-sm font-bold" data-testid={`admin-category-chip-${category.slug}`}>{category.tamil_name}</span>)}</div>
            </form>
            <div className="border border-slate-200 bg-white p-5" data-testid="admin-subscriber-panel"><h3 className="text-2xl font-black" data-testid="admin-subscriber-title">Subscribers</h3><div className="mt-4 max-h-52 space-y-2 overflow-auto" data-testid="admin-subscriber-list">{subscribers.map((item) => <p key={item.id} className="text-sm font-bold text-slate-700" data-testid={`admin-subscriber-${item.id}`}>{item.email}</p>)}</div></div>
          </aside>
        </section>

        <section className="border border-slate-200 bg-white p-5 sm:p-6" data-testid="admin-ad-section">
          <div className="grid gap-6 lg:grid-cols-[1fr_1fr]" data-testid="admin-ad-layout">
            <form onSubmit={saveAd} className="grid gap-4" data-testid="admin-ad-form">
              <h2 className="text-3xl font-black text-slate-950" data-testid="admin-ad-form-title">Front Page Advertisement</h2>
              <input value={adForm.title} onChange={(e) => setAdForm({ ...adForm, title: e.target.value })} required placeholder="Advertiser headline" className="border border-slate-300 px-4 py-3 text-xl font-black focus:outline-none focus:ring-2 focus:ring-blue-900" data-testid="admin-ad-title-input" />
              <textarea value={adForm.description} onChange={(e) => setAdForm({ ...adForm, description: e.target.value })} placeholder="Short ad description" rows="3" className="border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-900" data-testid="admin-ad-description-input" />
              <input value={adForm.link_url} onChange={(e) => setAdForm({ ...adForm, link_url: e.target.value })} placeholder="https://advertiser-website.com" className="border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-900" data-testid="admin-ad-link-input" />
              <label className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-green-800 bg-green-50 px-4 py-5 font-black text-green-900" data-testid="admin-ad-upload-label">
                <Upload className="h-5 w-5" aria-hidden="true" />{adForm.media_id ? "Banner image selected" : "Upload Banner Image"}
                <input type="file" accept="image/*" onChange={uploadAdMedia} className="hidden" data-testid="admin-ad-media-upload-input" />
              </label>
              <label className="inline-flex items-center gap-2 font-bold" data-testid="admin-ad-active-label"><input type="checkbox" checked={adForm.active} onChange={(e) => setAdForm({ ...adForm, active: e.target.checked })} data-testid="admin-ad-active-checkbox" />Active on homepage</label>
              <div className="flex flex-wrap gap-3">
                <button type="submit" className="inline-flex w-fit items-center gap-2 bg-green-800 px-5 py-3 font-black text-white transition-colors duration-200 hover:bg-green-700" data-testid="admin-save-ad-button"><Save className="h-5 w-5" aria-hidden="true" />{editingAdId ? "Update Advertisement" : "Publish Advertisement"}</button>
                {editingAdId && <button type="button" onClick={() => { setEditingAdId(null); setAdForm({ title: "", description: "", link_url: "", media_id: "", active: true }); }} className="border border-slate-300 px-5 py-3 font-black" data-testid="admin-cancel-ad-edit-button">Cancel</button>}
              </div>
            </form>
            <div data-testid="admin-ad-list-panel">
              <h3 className="text-2xl font-black" data-testid="admin-ad-list-title">Current Ads</h3>
              <div className="mt-4 space-y-3" data-testid="admin-ad-list">
                {ads.length === 0 && <p className="border border-slate-200 bg-slate-50 p-4 font-bold text-slate-600" data-testid="admin-ad-empty-state">No front page advertisements yet.</p>}
                {ads.map((ad) => <div key={ad.id} className="grid gap-3 border border-slate-200 p-4" data-testid={`admin-ad-item-${ad.id}`}><div><p className="font-black text-slate-950" data-testid={`admin-ad-item-title-${ad.id}`}>{ad.title}</p><p className="text-sm font-bold text-slate-500" data-testid={`admin-ad-item-status-${ad.id}`}>{ad.active ? "Active" : "Inactive"}</p>{ad.media?.url && <img src={mediaUrl(ad.media.url)} alt={ad.title} className="mt-3 aspect-[16/5] w-full object-cover" data-testid={`admin-ad-item-image-${ad.id}`} />}</div><div className="flex gap-2"><button onClick={() => editAd(ad)} className="border border-blue-900 px-3 py-2 font-black text-blue-900" data-testid={`admin-edit-ad-${ad.id}`}>Edit</button><button onClick={() => deleteAd(ad.id)} className="border border-red-700 px-3 py-2 font-black text-red-700" data-testid={`admin-delete-ad-${ad.id}`}>Delete</button></div></div>)}
              </div>
            </div>
          </div>
        </section>

        <section className="border border-slate-200 bg-white p-5 sm:p-6" data-testid="admin-posts-section">
          <h2 className="text-3xl font-black" data-testid="admin-posts-title">Posts</h2>
          <div className="mt-5 overflow-x-auto" data-testid="admin-posts-table-wrap">
            <table className="w-full min-w-[760px] text-left" data-testid="admin-posts-table">
              <thead><tr className="border-b border-slate-200 text-sm font-black text-slate-500"><th className="py-3" data-testid="admin-post-table-title-head">Title</th><th data-testid="admin-post-table-status-head">Status</th><th data-testid="admin-post-table-views-head">Views</th><th data-testid="admin-post-table-actions-head">Actions</th></tr></thead>
              <tbody>{posts.map((post) => <tr key={post.id} className="border-b border-slate-100" data-testid={`admin-post-row-${post.id}`}><td className="py-4 font-black" data-testid={`admin-post-row-title-${post.id}`}>{post.title}</td><td data-testid={`admin-post-row-status-${post.id}`}>{post.status}</td><td data-testid={`admin-post-row-views-${post.id}`}>{post.views}</td><td><div className="flex gap-2"><a href={`/news/${post.slug}`} target="_blank" rel="noreferrer" className="border border-slate-300 p-2" data-testid={`admin-view-post-${post.id}`}><Eye className="h-4 w-4" /></a><button onClick={() => editPost(post)} className="border border-blue-900 p-2 text-blue-900" data-testid={`admin-edit-post-${post.id}`}><Edit className="h-4 w-4" /></button><button onClick={() => deletePost(post.id)} className="border border-red-700 p-2 text-red-700" data-testid={`admin-delete-post-${post.id}`}><Trash2 className="h-4 w-4" /></button></div></td></tr>)}</tbody>
            </table>
          </div>
        </section>

        <section className="border border-slate-200 bg-white p-5 sm:p-6" data-testid="admin-comments-section">
          <h2 className="text-3xl font-black" data-testid="admin-comments-title">Comment Moderation</h2>
          <div className="mt-5 grid gap-4" data-testid="admin-comments-list">
            {comments.map((item) => <div key={item.id} className="grid gap-3 border border-slate-200 p-4 lg:grid-cols-[1fr_auto]" data-testid={`admin-comment-${item.id}`}><div><p className="font-black" data-testid={`admin-comment-name-${item.id}`}>{item.name} • {item.status}</p><p className="text-sm text-slate-500" data-testid={`admin-comment-post-${item.id}`}>{item.post_title}</p><p className="mt-2 text-slate-700" data-testid={`admin-comment-body-${item.id}`}>{item.body}</p></div><div className="flex gap-2"><button onClick={() => moderate(item.id, "approved")} className="bg-green-800 px-4 py-2 font-black text-white" data-testid={`admin-approve-comment-${item.id}`}>Approve</button><button onClick={() => moderate(item.id, "rejected")} className="bg-red-700 px-4 py-2 font-black text-white" data-testid={`admin-reject-comment-${item.id}`}>Reject</button></div></div>)}
          </div>
        </section>
      </main>
    </div>
  );
}